import { useCategoryGroupedProducts } from '@/components/catalogue/products/useFilteredProducts';
import { CAPITAL_PURCHASE_NAME } from '@/pages/customer/financeModels/const';
import {
  constructNameFromTypeAndTerm,
  constructPeripheralNameFromTypeAndTerm
} from '@/pages/settings/bulk/helpers/utils';
import { useDeviceFeeById } from '@/utils/deviceFee';
import { usePrintCurrencyStringForCurrentCustomer } from '@/utils/price';
import { Value } from '@udecode/plate-common';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { StoreApi, create, useStore } from 'zustand';
import { Contact } from '../api/commonTypes';
import { PurchaseOrderDeliveryOption } from '../api/contracts/catalog/bundles';
import { Product } from '../api/contracts/catalog/types';
import { FinanceModel, PeripheralFinanceModel } from '../api/contracts/financemodels';
import { useCategories } from '../api/hooks/catalog';
import { useFinanceModels, usePeripheralFinanceModels } from '../api/hooks/financemodels';
import { useProducts } from '../api/hooks/product';
import { PODeliveryAddress } from './purchaseOrder.types';

export interface SelectedProduct {
  productId: string;
  quantity: number;
}

export interface PoDelivery {
  type: PurchaseOrderDeliveryOption;
  addressId?: string;
}

export interface PoContact {
  type: PurchaseOrderDeliveryOption;
  contactId?: string;
}

export enum PoStatus {
  Draft = 0,
  WarehouseProcessing = 1,
  InTransit = 2,
  Delivered = 3,
  Complete = 4
}

export interface PurchaseOrderInfo {
  orderName?: string;
  selectedProducts: SelectedProduct[];
  selectProduct: (productId: string) => void;
  setProductQuantity: (productId: string, quantity: number) => void;
  selectedFinanceModels: FinanceModel[];
  selectedPeripheralFinanceModel?: PeripheralFinanceModel;
  selectCapitalPurchaseForMainDevice: (categoryId: string) => void;
  selectCapitalPurchaseForPeripheral: () => void;
  selectFinanceModel: (models: FinanceModel[]) => void;
  selectPeripheralFinanceModel: (model: PeripheralFinanceModel) => void;
  savePurchaseOrder: (name: string, state: PoStatus) => Promise<void>;
  delivery?: PoDelivery;
  contact?: PoContact;
  storeDeliveryDetails: (
    type: PurchaseOrderDeliveryOption,
    customAddress?: PODeliveryAddress,
    updateBackend?: boolean
  ) => Promise<void>;
  storeContactDetails: (type: PurchaseOrderDeliveryOption, contact?: Contact, updateBackend?: boolean) => Promise<void>;
  clearDeliveryAddressId: () => void;
  status: PoStatus;
  setStatus: (status: PoStatus) => void;
  poRef?: string;
  stockingOrder?: string;
  partnerRef?: string;
  setPoRef: (val?: string) => void;
  setStockingOrder: (val?: string) => void;
  setPartnerRef: (val?: string) => void;
  selectedDeviceFeeId?: string;
  selectDeviceFee: (id: string) => void;
  submittedOnDate?: Date;
  notes?: {
    value: Value;
  };
  setNotes: (val?: { value: Value }) => void;
}

export const PurchaseOrderContext = createContext<StoreApi<PurchaseOrderInfo>>(
  create<PurchaseOrderInfo>(() => ({
    selectedProducts: [],
    selectProduct: () => {},
    selectedFinanceModels: [],
    selectedPeripheralFinanceModel: undefined,
    setProductQuantity: () => () => {},
    selectCapitalPurchaseForMainDevice: () => {},
    selectCapitalPurchaseForPeripheral: () => {},
    selectFinanceModel: () => {},
    selectPeripheralFinanceModel: () => {},
    savePurchaseOrder: async () => {},
    delivery: undefined,
    contact: undefined,
    storeDeliveryDetails: async () => {},
    storeContactDetails: async () => {},
    clearDeliveryAddressId: () => {},
    status: PoStatus.Draft,
    setStatus: () => {},
    setPartnerRef: () => {},
    setStockingOrder: () => {},
    setPoRef: () => {},
    selectDeviceFee: () => {},
    setNotes: () => {}
  }))
);

function usePurchaseOrderService<U>(selector: (store: PurchaseOrderInfo) => U): U | undefined {
  const context = useContext(PurchaseOrderContext);
  if (!context) {
    throw new Error('usePurchaseOrder must be used within a PurchaseOrderProvider');
  }
  return useStore(context, selector);
}

export const usePoClearDeliveryAddressId = () => {
  const clear = usePurchaseOrderService(state => state.clearDeliveryAddressId);
  if (!clear) {
    throw new Error('usePoClearDeliveryAddressId must be used within a PurchaseOrderProvider');
  }
  return clear;
};

export const usePoName = () => {
  const name = usePurchaseOrderService(state => state.orderName);

  return name;
};

export const usePoSelectedProductList = () => {
  const products = usePurchaseOrderService(state => state.selectedProducts);

  return products ?? [];
};

export const usePoSelectProduct = (product: Product) => {
  const selectId = usePurchaseOrderService(state => state.selectProduct);

  if (!selectId) {
    throw new Error('Missing product id');
  }

  return useCallback(() => selectId(product.id), [selectId, product]);
};

export const usePOIsProductSelected = (product: Product) => {
  const products = usePoSelectedProductList();

  return Boolean(products.find(item => item.productId === product.id));
};

export const usePoSetProductQuantity = (product: Product) => {
  const set = usePurchaseOrderService(state => state.setProductQuantity);

  if (!set) {
    throw new Error('Missing set function');
  }

  return useCallback((quanity: number) => set(product.id, quanity), [set, product]);
};

export const usePoSelectedProductQuantity = (product: Product) => {
  const list = usePoSelectedProductList();

  const selected = list.find(p => p.productId === product.id);

  return selected ? selected.quantity : undefined;
};

export const usePoSelectedProducts = () => {
  const [products] = useProducts();
  const selectedProductIds = usePoSelectedProductList().map(p => p.productId);

  return products.filter(p => selectedProductIds.includes(p.id));
};

export const usePoSelectedFinanceModels = () => {
  const selectedModels = usePurchaseOrderService(state => state.selectedFinanceModels);
  return selectedModels;
};

export const usePoSelectedFinanceModelForCategory = (categoryId: string) => {
  const selectedModels = usePoSelectedFinanceModels();
  const selectedModel = selectedModels?.find(m => m.categoryId === categoryId);

  if (!selectedModel) return;

  return selectedModel;
};

export const usePoSelectedFinanceModelNameForCategory = (categoryId: string) => {
  const selectedModel = usePoSelectedFinanceModelForCategory(categoryId);

  if (!selectedModel) return;

  return constructNameFromTypeAndTerm(selectedModel.financeModelType, selectedModel.term);
};

export const usePoSelectedPeripheralFinanceModel = () => {
  const selectedModel = usePurchaseOrderService(state => state.selectedPeripheralFinanceModel);
  return selectedModel;
};

export const usePoSelectedPeripheralFinanceModelName = () => {
  const selectedModel = usePoSelectedPeripheralFinanceModel();

  if (!selectedModel) return;

  return constructPeripheralNameFromTypeAndTerm(selectedModel.peripheralFinanceModelType, selectedModel.term);
};

export const usePoSelectCapitalPurchaseFinanceModelForMainDevice = () => {
  const select = usePurchaseOrderService(state => state.selectCapitalPurchaseForMainDevice);

  if (!select) {
    throw Error('selectCapitalPurchaseForMainDevice must be used within a CustomerCatalogueContextProvider');
  }

  return select;
};

export const usePoSelectCapitalPurchaseFinanceModelForPeripheral = () => {
  const select = usePurchaseOrderService(state => state.selectCapitalPurchaseForPeripheral);

  if (!select) {
    throw Error(
      'usePoSelectCapitalPurchaseFinanceModelForPeripheral must be used within a CustomerCatalogueContextProvider'
    );
  }

  return select;
};

export const usePoSelectFinanceModel = () => {
  const select = usePurchaseOrderService(state => state.selectFinanceModel);

  if (!select) {
    throw Error('usePoSelectFinanceModel must be used within a CustomerCatalogueContextProvider');
  }

  return select;
};

export const usePoSelectPeripheralFinanceModel = () => {
  const select = usePurchaseOrderService(state => state.selectPeripheralFinanceModel);

  if (!select) {
    throw Error('usePoSelectPeripheralFinanceModel must be used within a CustomerCatalogueContextProvider');
  }

  return select;
};

export const useSavePurchaseOrder = () => {
  const save = usePurchaseOrderService(state => state.savePurchaseOrder);

  if (!save) {
    throw Error('useSavePurchaseOrder must be used within a CustomerCatalogueContextProvider');
  }

  return save;
};

export const useConfirmPurchaseOrder = (nameToSave: string) => {
  const save = usePurchaseOrderService(state => state.savePurchaseOrder);
  const setStatus = usePurchaseOrderService(state => state.setStatus);

  return useCallback(async () => {
    if (!save || !setStatus) {
      throw Error('useSavePurchaseOrder must be used within a CustomerCatalogueContextProvider');
    }

    setStatus(PoStatus.WarehouseProcessing);
    await save(nameToSave, PoStatus.WarehouseProcessing);
  }, [save, setStatus, nameToSave]);
};

export const useStorePurchaseOrderDeliveryAddress = () => {
  const save = usePurchaseOrderService(state => state.storeDeliveryDetails);

  if (!save) {
    throw Error('useStorePurchaseOrderDeliveryAddress must be used within a CustomerCatalogueContextProvider');
  }

  return save;
};

export const useStorePurchaseOrderDeliveryContact = () => {
  const save = usePurchaseOrderService(state => state.storeContactDetails);

  if (!save) {
    throw Error('useStorePurchaseOrderDeliveryContact must be used within a CustomerCatalogueContextProvider');
  }

  return save;
};

export const usePoDelivery = () => {
  return usePurchaseOrderService(state => state.delivery);
};

export const usePoContact = () => {
  return usePurchaseOrderService(state => state.contact);
};

export const useAllPoSelectedDevices = () => {
  const selectedProducts = usePoSelectedProductList();

  const submittedOnDate = usePurchaseOrderService(state => state.submittedOnDate);

  const groups = useCategoryGroupedProducts({
    filterProductIds: selectedProducts.map(product => product.productId),
    excludeBBBFilter: true,
    effectiveDate: submittedOnDate
  });

  const flatProductArray = Object.values(groups).flatMap(items => items) as Product[];
  return flatProductArray.map(product => {
    const lookup = selectedProducts.find(item => item.productId === product.id);

    return {
      ...product,
      quantity: lookup?.quantity ?? 1
    };
  });
};

const usePoDevices = (mainDevices: boolean) => {
  const products = useAllPoSelectedDevices();

  return products.filter(product => (mainDevices ? product.category.isMainDevice : !product.category.isMainDevice));
};

export const usePoSelectedMainDevices = () => {
  return usePoDevices(true);
};

export const usePoSelectedPeripherals = () => {
  return usePoDevices(false);
};

export const usePoSelectedDevicesGroupedByCategory = () => {
  const selectedProducts = usePoSelectedProductList();

  const groups = useCategoryGroupedProducts({
    filterProductIds: selectedProducts.map(product => product.productId),
    excludeBBBFilter: true
  });

  return groups;
};

export const usePOGetTotalDeviceFee = () => {
  const deviceFee = usePoDeviceFee();
  const mainDevices = usePoSelectedMainDevices();

  const totalDeviceFee = useMemo(() => {
    if (!deviceFee) {
      return 0;
    }

    let total = 0;
    mainDevices.forEach(device => {
      total += device.quantity * deviceFee;
    });

    return total;
  }, [mainDevices, deviceFee]);

  return totalDeviceFee;
};

export const usePOGetTotalCosts = () => {
  const mainDevices = usePoDevices(true);
  const peripherals = usePoDevices(false);
  const models = usePoSelectedFinanceModels();
  const peripheralModel = usePoSelectedPeripheralFinanceModel();
  const deviceFee = usePoDeviceFee();
  const totalDeviceFee = usePOGetTotalDeviceFee();

  const totalCost = useMemo(() => {
    let total = 0;
    mainDevices.forEach(device => {
      const deviceTotal = device.quantity * (device.price?.price || 0);
      total += deviceTotal;

      const selectedModel = models?.find(
        m => m.categoryId === device.category.id && m.manufacturerId === device.manufacturer.id
      );
    });

    peripherals.forEach(peripheral => {
      const deviceTotal = peripheral.quantity * (peripheral.price?.price || 0);
      total += deviceTotal;
    });

    const selectedModel = models ? models[0] : peripheralModel;
    const pcmTotal = (total + (totalDeviceFee ?? 0)) * (selectedModel?.pcmFactor ?? 0);

    return { total, pcmTotal, totalDeviceFee };
  }, [mainDevices, peripherals, models]);

  return totalCost;
};

export const useSetPurchaseOrderStatus = () => {
  const save = usePurchaseOrderService(state => state.setStatus);

  if (!save) {
    throw Error('useSetPurchaseOrderStatus must be used within a CustomerCatalogueContextProvider');
  }

  return save;
};

export const usePoDetails = () => {
  const poRef = usePurchaseOrderService(s => s.poRef);
  const partnerRef = usePurchaseOrderService(s => s.partnerRef);
  const stockingOrder = usePurchaseOrderService(s => s.stockingOrder);
  const notes = usePurchaseOrderService(s => s.notes);

  return { poRef, partnerRef, stockingOrder, notes };
};

export const useSetPoDetails = () => {
  const setPoRef = usePurchaseOrderService(s => s.setPoRef);
  const setPartnerRef = usePurchaseOrderService(s => s.setPartnerRef);
  const setStockingOrder = usePurchaseOrderService(s => s.setStockingOrder);
  const setNotes = usePurchaseOrderService(s => s.setNotes);

  if (!setPoRef || !setPartnerRef || !setStockingOrder || !setNotes) {
    throw Error('useSetPoDetails must be used within a PurchaseOrderProvider');
  }

  return { setPoRef, setPartnerRef, setStockingOrder, setNotes };
};

export const usePoSelectedDeviceFee = () => {
  const selectedDeviceFeeId = usePurchaseOrderService(state => state.selectedDeviceFeeId);
  const submittedOnDate = usePurchaseOrderService(state => state.submittedOnDate);

  const deviceFee = useDeviceFeeById(selectedDeviceFeeId, submittedOnDate);
  return deviceFee;
};

export const usePoSelectDeviceFee = () => {
  const select = usePurchaseOrderService(state => state.selectDeviceFee);

  if (!select) {
    throw Error('usePoSelectDeviceFee must be used within a CustomerCatalogueContextProvider');
  }

  return select;
};

export const usePoDeviceFee = () => {
  const deviceFee = usePoSelectedDeviceFee();

  return deviceFee?.price?.price;
};

export const usePoSelectGlobalFinanceModel = () => {
  const [categories] = useCategories();

  const selectModel = usePoSelectFinanceModel();
  const selectCapitalPurchase = usePoSelectCapitalPurchaseFinanceModelForMainDevice();

  const selectPeripheralModel = usePoSelectPeripheralFinanceModel();
  const selectPeripheralCapitalPurchase = usePoSelectCapitalPurchaseFinanceModelForPeripheral();

  const mainDeviceModels = useFinanceModels();
  const peripheralModels = usePeripheralFinanceModels();

  return useCallback(
    (modelName: string) => {
      if (modelName === CAPITAL_PURCHASE_NAME) {
        categories.forEach(c => {
          selectCapitalPurchase(c.id);
        });
        selectPeripheralCapitalPurchase();
        return;
      }

      const mainDeviceModelLookups = mainDeviceModels.filter(m => m.name === modelName);
      const peripheralModelLookup = peripheralModels.find(m => m.name === modelName);

      if (mainDeviceModelLookups.length === 0 || !peripheralModelLookup)
        throw new Error(`Model lookup for ${modelName} failed. Please contact a DaaS admin`);
      selectModel(mainDeviceModelLookups);
      selectPeripheralModel(peripheralModelLookup);
    },
    [selectModel, selectCapitalPurchase, selectPeripheralModel, selectPeripheralCapitalPurchase, categories]
  );
};

export const usePoFormattedTotalCosts = () => {
  const { total, pcmTotal, totalDeviceFee } = usePOGetTotalCosts();

  const printCurrencyString = usePrintCurrencyStringForCurrentCustomer();
  const price = printCurrencyString(total);
  const pcm = printCurrencyString(pcmTotal);
  const deviceFee = printCurrencyString(totalDeviceFee);
  const totalProductAndDeviceFeePrice = printCurrencyString(total + totalDeviceFee);

  return {
    totalProductPrice: price,
    totalPcm: pcmTotal ? pcm : null,
    totalDeviceFee: deviceFee,
    totalProductAndDeviceFeePrice
  };
};
